import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useUpdateEvent } from '@/hooks/useEvents'
import { EVENT_NOTES_MAX } from '@lib/input/event.input'
import type { EventDetailDto } from '@lib/dto/event.dto'

/**
 * Venue staff's own notes about an event. Clients, planners and vendors never
 * see them: the API sends them null, and this section is only on staff's view.
 * Staff whose role only views events read them.
 *
 * Mounted with `key={event.notes}`, so a saved change starts the draft afresh
 * rather than being copied in by an effect.
 */
export function EventNotesEditor({
  event,
  canEdit,
}: {
  event: EventDetailDto
  /** Whether the viewer can edit events, and so write the notes rather than read them. */
  canEdit: boolean
}) {
  const update = useUpdateEvent(event.id)
  const [draft, setDraft] = React.useState(event.notes ?? '')
  const [failure, setFailure] = React.useState<string | null>(null)
  // Stored trimmed, and empty as none, so that is what "anything to save" compares.
  const dirty = draft.trim() !== (event.notes ?? '')

  function save(): void {
    setFailure(null)
    const trimmed = draft.trim()
    update.mutate({ notes: trimmed === '' ? null : trimmed }, { onError: (e) => setFailure(requestErrorMessage(e)) })
  }

  if (!canEdit) {
    return event.notes ? (
      <p className="m-0 whitespace-pre-wrap text-sm">{event.notes}</p>
    ) : (
      <p className="m-0 text-sm text-ink-muted">No notes.</p>
    )
  }

  return (
    <div className="space-y-3">
      <Textarea
        aria-label="Notes"
        rows={6}
        value={draft}
        maxLength={EVENT_NOTES_MAX}
        placeholder="Anything the team should know about this event."
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex items-center gap-3">
        {/* The label carries the state: "Save" while there is something to save, "Saved" when not. */}
        <Button size="sm" disabled={!dirty || update.isPending} onClick={save}>
          {update.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </Button>
        {failure ? <FormError>{failure}</FormError> : null}
      </div>
    </div>
  )
}
